/* eslint-disable */
import { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  makeStyles,
  ListItem,
  ListItemIcon,
  ListItemText,
  List,
  Tooltip,
} from '@material-ui/core';
import cx from 'classnames';
import { alpha } from '@material-ui/core/styles';
import { useSelector, useDispatch } from 'react-redux';
import { navigateToRoute, selectBreadcrumbs } from '../../../../../../redux_store';
import { checkPermission } from '../../../../../../helpers/checkPermissions';

const useStyles = makeStyles((theme) => ({
  navItem: {
    color: theme.colors.gray1,
    textTransform: 'none',
    fontWeight: '500',
    overflow: 'hidden',
    borderRadius: 8,
    margin: theme.spacing(0.25, 1, 0.25, 0.5),
    minHeight: 36,
    width: 'auto',

    '&:hover': {
      background: 'rgba(255, 255, 255, 0.1)',
      borderRadius: 8,
    },
  },
  navItemChild: {
    padding: '4px 1px 4px 60px !important',
    color: theme.palette.common.white,
    textTransform: 'none',
    transition: 'margin 0.3s ease-in, padding 0.3s ease-in, opacity 0.3s ease-in',

    '&::before': {
      content: '""',
      position: 'absolute',
      left: theme.spacing(4.2),
      top: '50%',
      transform: 'translateY(-50%)',
      width: 4,
      height: 4,
      borderRadius: '50%',
      backgroundColor: 'rgba(255, 255, 255, 0.6)',
    },
  },
  sideMenuPinnedChild: {
    transition: 'margin 0.3s ease-in, padding 0.3s ease-in, opacity 0.3s ease-in',
    marginLeft: -28,
  },
  listItemText: {
    fontSize: 14,
    fontWeight: 500,
    letterSpacing: 0.5,
    opacity: 0.8,
    color: 'rgba(255, 255, 255, 0.8)',
    overflow: 'hidden',
    whiteSpace: 'normal',
    minWidth: 120,
  },
  selectedItemText: {
    transition: 'padding 0.3s ease-in, opacity 0.3s ease-in',
    opacity: 1,
  },
  sideMenuPinned: {
    transition: 'padding 0.3s ease-in, opacity 0.3s ease-in',
    opacity: 0,
  },
  selectedItem: {
    background: `linear-gradient(90deg, ${alpha(theme.colors.blue, 0)} 0%,
      ${theme.colors.blue3} 99%)`,
  },
  selectedChildItem: {
    transition: 'margin 0.3s ease-in, padding 0.3s ease-in, opacity 0.3s ease-in',
    background: `linear-gradient(90deg, ${alpha(theme.colors.blue, 0)} 10%,
      ${alpha(theme.colors.blue3, .6)} 99%)`,
  },
  simpleIcon: {
    margin: theme.spacing(0.5),
    width: 16,
    height: 16,
    borderRadius: '50%',
    backgroundColor: theme.colors.gray1,
  },
  navIcon: {
    width: 16,
    height: 16,
  },
  listItemIcon: {
    fill: 'rgba(255, 255, 255, 0.6)',
    color: 'rgba(255, 255, 255, 0.6)',
    minWidth: 32,
    display: 'flex',
    justifyContent: 'center',
    marginRight: 4,

    '& svg': {
      width: 16,
      height: 16,
    },
  },
  selectedListItemIcon: {
    fill: 'rgba(255, 255, 255, 1)',
    color: 'rgba(255, 255, 255, 1)',

    '& svg': {
      width: 16,
      height: 16,
    },
  },
  dashboardIcon: {
    '& svg': {
      width: 12,
      height: 12,
    },
  },
}));

const NavItem = ({ item, isChild, disabled }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const breadcrumbs = useSelector(selectBreadcrumbs) || [];
  const { isSideMenuPinned } = useSelector((state) => state.appSettings);
  const { screenHeight } = useSelector((state) => state.appSettings);
  const { metadata: { currentAccount, currentAccountPermissions }, } = useSelector((state) => state.user);
  const { disabledFeatures } = useSelector((state) => state.appSettings);

  const openInNewTab = (url) => {
    const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
    if (newWindow) newWindow.opener = null;
  };

  const handleClick = () => {
    if (item.route.externalReference) {
      openInNewTab(item.route.path);
    } else {
      dispatch(navigateToRoute({
        route: {
          path: item.route.path,
          title: item.route.additional.title,
          clearBreadcrumbs: true,
          params: {
            currentAccountUuid: currentAccount?.uuid,
        },
      }}));
    }
  };

  const selected = useMemo(
    () => ((breadcrumbs[0]?.title &&
      (breadcrumbs[0].title === item.name ||
        breadcrumbs[0].title === item.route.additional.title))),
    [breadcrumbs[0]?.title, item],
  );

  const childSelected = useMemo(
    () => (item.children && item.children.map((item) => item?.route?.additional?.title).includes(breadcrumbs[0]?.title)),
    [breadcrumbs[0]?.title, item],
  );

  const ItemRouteIcon = item.route && item.route.additional.icon;
  const icon = ItemRouteIcon ? (
    <ItemRouteIcon
      fill={(childSelected || selected) ? null : alpha('#FFF', 0.3)}
      className={ItemRouteIcon.name === 'DashboardLogo' ? classes.dashboardIcon : classes.navIcon}
    />
  ) : (
    <div className={classes.simpleIcon} />
  );

  const _itemText = item.name || item.route.additional.title;
  const text = !isSideMenuPinned && isChild ? _itemText.substring(0, 1) : _itemText;

  return (
    <>
      <Tooltip
        disableFocusListener={isSideMenuPinned}
        disableHoverListener={isSideMenuPinned}
        disableTouchListener={isSideMenuPinned}
        title={_itemText}
        placement={'right'}
        style={{
          zIndex: 100,
        }}
      >
        <ListItem
          button
          style={{
            padding: '6px 8px',
          }}
          className={cx(classes.navItem, {
            [classes.navItemChild]: isChild,
            [classes.selectedItem]: selected || childSelected,
            [classes.selectedChildItem]: isChild && selected,
            [classes.sideMenuPinnedChild]: isChild && !isSideMenuPinned,
          })}
          onClick={handleClick}
          disabled={disabled}
        >
          {!isChild && (
            <ListItemIcon className={cx(classes.listItemIcon, {
              [classes.selectedListItemIcon]: childSelected || selected,
              [classes.dashboardIcon]: ItemRouteIcon && ItemRouteIcon.name === 'DashboardLogo',
            })}>{icon}</ListItemIcon>
          )}
          <ListItemText
            primary={text}
            primaryTypographyProps={{
              className: cx(classes.listItemText, {
                [classes.selectedItemText]: selected || childSelected,
                [classes.sideMenuPinned]: !isChild && !isSideMenuPinned,
              }),
            }}
          />
        </ListItem>
      </Tooltip>
      {item.children && (
        <List component='div' disablePadding>
          {item.children.map((childItem) => {
            const isAllowed =
              checkPermission(
                currentAccountPermissions,
                childItem.route.additional.permission,
              ) && !disabledFeatures.includes(childItem.route.additional.disabledId);

            return isAllowed ? <div style={{
              display: 'flex',
            }}>
              <div style={{
                height: !isSideMenuPinned ? 12 : childItem.id === item.children[item.children.length - 1].id ? 22 : 47,
                marginLeft: 33,
                borderLeft: '1px solid rgba(240, 248, 255, 0.35)',
                transition: 'all 0.3s linear',
                marginTop: !isSideMenuPinned ? -6 : 0,
              }} />
              <NavItem key={childItem.id}
                       item={childItem}
                       isChild
              />
            </div> : null;
          })}
        </List>
      )}
    </>
  );
};

NavItem.propTypes = {
  item: PropTypes.shape({
    route: PropTypes.object,
    name: PropTypes.string,
    children: PropTypes.array,
  }).isRequired,
  isChild: PropTypes.bool,
  disabled: PropTypes.bool,
};

NavItem.defaultProps = {
  isChild: false,
  disabled: false,
};

export default NavItem;
